/**
 * KAN-880: чистые функции таблицы лидов CRM (GET /api/v1/crm/leads/,
 * страницы offset/limit в кеше useInfiniteQuery): строки таблицы, подписи
 * колонок и дней, параметры запроса из адреса, оптимистичные действия и
 * правка строки в кеше.
 */
import type { InfiniteData } from '@tanstack/react-query';
import type { AttentionAction } from '@/services/api/dashboard';
import type { CrmLeadFilterParams, CrmTableOutcome } from '@/types/crmPipeline';
import type { CrmLeadRow, CrmLeadsPage, CrmLeadsParams, CrmOutcome, CrmRow, CrmTableSort } from '@/types/crm';
import type { CrmRange, CrmTableRow } from './crmTable';
import { daysInWorkText, daysOnStageText } from './crmPipeline';

export type CrmLeadsData = InfiniteData<CrmLeadsPage>;

/** Строк на страницу таблицы (backend принимает 1..500). */
export const CRM_LEADS_PAGE_SIZE = 100;

/** Фильтры таблицы без offset/limit: исход, общие фильтры, атрибут, организация. */
export type CrmLeadsQuery = Omit<CrmLeadsParams, 'offset' | 'limit'>;

/**
 * Параметры таблицы из состояния страницы: исход, общие фильтры, атрибут (?attr=&attr_value=),
 * организация (?company_id=), KAN-885: период взятия в работу (range null - без дат),
 * KAN-887: сортировка (если задана); KAN-883/891/892: событие периода, lead=1, idle=1;
 * KAN-898: junk - только контакты в мусоре.
 */
export function crmLeadsQuery(
  outcome: CrmTableOutcome,
  filters: CrmLeadFilterParams,
  url: { attr: string | null; attrValue: string | null; companyId: string | null },
  range: CrmRange | null = null,
  sort: CrmTableSort | null = null,
  flags: Pick<CrmLeadsParams, 'event' | 'lead' | 'idle'> = {},
  junk = false
): CrmLeadsQuery {
  const query: CrmLeadsQuery = { outcome, ...filters, ...flags };
  if (junk) query.junk = '1';
  if (sort) query.sort = sort;
  if (range) {
    query.date_from = range.from;
    query.date_to = range.to;
  }
  if (url.attr) {
    query.attr = url.attr;
    if (url.attrValue) query.attr_value = url.attrValue;
  }
  if (url.companyId) query.company_id = url.companyId;
  return query;
}

/** Все загруженные строки страниц как строки таблицы CRM. */
export function leadTableRows(data: CrmLeadsData | undefined): CrmTableRow[] {
  return (data?.pages ?? []).flatMap((page) => page.rows.map((row): CrmTableRow => ({ kind: 'lead', row, day: null })));
}

/** Подписи колонок доски по key (из первой страницы). */
export function leadColumnLabels(data: CrmLeadsData | undefined): Record<string, string> {
  const columns = data?.pages[0]?.columns ?? [];
  return Object.fromEntries(columns.map((c) => [c.key, c.label]));
}

/** Дни строки: у взятых "N дней в работе", иначе "N дней на этапе"; нет данных: null. */
export function leadDaysText(row: CrmRow): string | null {
  const lead = row as Partial<CrmLeadRow>;
  return lead.taken_at ? daysInWorkText(lead.days_in_work) : daysOnStageText(lead.days_on_stage);
}

function mapRows(data: CrmLeadsData, update: (row: CrmLeadRow) => CrmLeadRow, clientId: string): CrmLeadsData {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      rows: page.rows.map((row) => (row.client_id === clientId ? update(row) : row)),
    })),
  };
}

/** Правка строки в кеше таблицы; ответ PATCH (строка доски) сливается с полями колонки. */
export function replaceLeadRow(
  data: CrmLeadsData,
  clientId: string,
  update: (row: CrmRow) => CrmRow
): CrmLeadsData {
  return mapRows(data, (row) => ({ ...row, ...update(row) }), clientId);
}

/** Строка в кеше таблиц лидов (для отката правки). */
export function findLeadRow(data: CrmLeadsData | undefined, clientId: string): CrmLeadRow | undefined {
  return data?.pages.flatMap((p) => p.rows).find((row) => row.client_id === clientId);
}

/** Ответ 400 с ошибкой параметра field ({field: [текст]}): например устаревший ?attr= в адресе. */
export function isParamError(error: unknown, field: string): boolean {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
  const data = response?.data;
  return response?.status === 400 && typeof data === 'object' && data !== null && field in data;
}

const ACTION_OUTCOME: Partial<Record<AttentionAction, CrmOutcome>> = { success: 'won', refused: 'lost' };

/**
 * Оптимистичное действие над строкой: "Взять в работу" ставит метку взятия
 * (кнопка взятия пропадает), "Успех"/"Не успех" проставляют исход (кнопок нет).
 */
export function applyLeadAction(data: CrmLeadsData, clientId: string, action: AttentionAction): CrmLeadsData {
  const outcome = ACTION_OUTCOME[action];
  if (outcome) return mapRows(data, (row) => ({ ...row, outcome, attention: false }), clientId);
  // KAN-955: "Работа идёт" только снимает строку из внимания (взятие и исход не меняются).
  if (action === 'work_continues') return mapRows(data, (row) => ({ ...row, attention: false, idle: false }), clientId);
  const taken = data.pages[0]?.columns.find((c) => c.kind === 'taken')?.key;
  return mapRows(
    data,
    (row) => ({
      ...row,
      attention: false,
      taken_at: row.taken_at ?? new Date().toISOString(),
      days_in_work: row.days_in_work ?? 0,
      column: taken ?? row.column,
    }),
    clientId
  );
}
